import { Checkbox } from '@heroui/react'
import { screenDisabled, screenMark, screenTicked, toggleScreen } from '../lib/permissions'
import { hasPhrase, type StaffKey } from '../lib/staffText'
import type { Permission, Screen } from '../lib/types'
import { useStaffText } from '../ui/StaffText'
import '../styles/people.css'

/**
 * The screen tick boxes (HeroUI Checkbox), grouped Daily work, Management, Settings. With
 * `baseline` a box marks "+" or "−" against the role; a box beyond `mayGive` is disabled.
 */

const GROUPS: Array<{ key: Screen['group']; label: StaffKey }> = [
  { key: 'daily', label: 'admin.roles.group.daily' },
  { key: 'manage', label: 'admin.roles.group.manage' },
  { key: 'settings', label: 'admin.roles.group.settings' },
]

/** admin.roles.screen.<key> for every screen org.Screens names (see roles.ts).
 *  A key this build has no words for yet — a module shipped ahead of its
 *  translations — shows its raw name rather than throwing. */
export function screenLabel(say: (key: StaffKey) => string, key: string): string {
  const phraseKey = `admin.roles.screen.${key}` as StaffKey
  return hasPhrase(phraseKey) ? say(phraseKey) : key
}

export function PermissionBoxes({
  screens,
  value,
  onChange,
  disabled,
  baseline,
  mayGive,
}: {
  screens: Screen[]
  value: Permission[]
  onChange: (next: Permission[]) => void
  disabled?: boolean
  baseline?: Permission[]
  mayGive: Permission[]
}) {
  const { say } = useStaffText()

  return (
    <>
      {GROUPS.map(({ key, label }) => {
        const inGroup = screens.filter((s) => s.group === key)
        if (inGroup.length === 0) return null
        return (
          <fieldset className="pk-perm" key={key}>
            <legend>{say(label)}</legend>
            <div className="pk-checks">
              {inGroup.map((screen) => {
                const ticked = screenTicked(screen, value)
                const mark = screenMark(screen, value, baseline)
                const boxDisabled = disabled || screenDisabled(screen, mayGive)
                return (
                  <Checkbox
                    variant="secondary"
                    key={screen.key}
                    className="pk-check-box"
                    isSelected={ticked}
                    isDisabled={boxDisabled}
                    onChange={() => onChange(toggleScreen(screen, value, !ticked))}
                  >
                    {/* label.pk-check with data-mark: the e2e suite finds a box by it. */}
                    <Checkbox.Content className="pk-check" data-mark={mark || undefined}>
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <span className="pk-check__name">{screenLabel(say, screen.key)}</span>
                      {mark && <span className="pk-check__mark">{mark}</span>}
                    </Checkbox.Content>
                  </Checkbox>
                )
              })}
            </div>
          </fieldset>
        )
      })}
    </>
  )
}
